import { type SWRInfiniteConfiguration } from 'swr/infinite';
import {
  type PaginationQuery,
  AccountTransaction,
} from '@mls-wallet/shared-types';
import { type PaginatedResponse, usePagination } from '@/hooks/use-pagination';

type HistoricTransactionsResponse = PaginatedResponse<AccountTransaction>;

/**
 * Historic-specific wrapper around the generic pagination hook.
 *
 * Notes:
 * - `refreshInterval` and the rest of the SWR options are passed through for
 *   hook compatibility with polling-based consumers.
 * - `enabled` lets tabbed UIs defer fetching until the historic tab is active.
 */
type UseHistoricTransactionsOptions = {
  refreshInterval?: SWRInfiniteConfiguration<HistoricTransactionsResponse>['refreshInterval'];
  pagination?: PaginationQuery;
  enabled?: boolean;
} & SWRInfiniteConfiguration<HistoricTransactionsResponse>;

/**
 * Fetch historic transactions from the session-auth endpoint.
 *
 * Historic remains on its own route so the backend router can inject the
 * fixed executed/historic listing parameters without leaking them into ui-web.
 */
export function useHistoricTransactions(
  options?: UseHistoricTransactionsOptions
) {
  const { pagination, enabled, ...swr } = options ?? {};

  const result = usePagination<AccountTransaction>({
    // Historic stays on its own endpoint so the router can inject the
    // fixed executed/historic use-case inputs without the UI knowing them.
    path: 'transactions/historic',
    ...(pagination ? { pagination } : {}),
    ...(enabled !== undefined ? { enabled } : {}),
    ...(Object.keys(swr).length > 0
      ? {
          swr: swr as SWRInfiniteConfiguration<HistoricTransactionsResponse>,
        }
      : {}),
  });

  return {
    transactions: result.data,
    pagination: result.pagination,
    hasMore: result.hasMore,
    isLoading: result.isLoading,
    isLoadingMore: result.isLoadingMore,
    error: result.error,
    loadMore: result.loadMore,
    refetch: result.refetch,
    reset: result.reset,
  };
}
